import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
  Space,
  Breadcrumb,
  Steps,
  Table,
  Statistic,
  Tooltip,
  Alert,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  FileTextOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const BulkCreatePurchaseOrder = () => {
  const navigate = useNavigate();
  const [spareparts, setSpareparts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [currentStep, _setCurrentStep] = useState(0);
  const [bulkItems, setBulkItems] = useState([]);
  const [minQty, setMinQty] = useState(10);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    const mockSuppliers = [
      { id: 1, name: 'Global Auto Parts Ltd.', contact: 'Mr. Lee', country: 'China' },
      { id: 2, name: 'Euro Spares Inc.', contact: 'Ms. Schmidt', country: 'Germany' },
      { id: 3, name: 'American Machinery Co.', contact: 'Mr. Smith', country: 'USA' },
    ];
    setSuppliers(mockSuppliers);
    fetchSpareparts();
  }, []);

  const fetchSpareparts = async () => {
    try {
      const res = await api.get('/api/spareparts');
      setSpareparts(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadLowStockItems = async () => {
    setBulkLoading(true);
    try {
      const res = await api.get('/api/inventories/low-stock', {
        params: { maxQuantity: minQty },
      });
      const items = (res.data || []).map((inv) => ({
        key: `${inv.sparepartId}-${inv.warehouseId || inv.id}`,
        inventoryId: inv.id,
        sparepartId: inv.sparepartId,
        currentQty: inv.quantity,
        warehouseId: inv.warehouseId,
        partName: inv.sparepart?.partName || 'N/A',
        partNumber: inv.sparepart?.partNumber || 'N/A',
        importerId: inv.sparepart?.importerId || 'N/A',
        measureUnit: inv.sparepart?.measureUnit || 'pcs',
        requiredQty: Math.max(minQty - inv.quantity, 1),
        unitCost: inv.sparepart?.price || 0,
        selected: true,
      }));
      setBulkItems(items);
      if (items.length === 0) {
        messageApi.info(`No items found with quantity ≤ ${minQty}`);
      }
    } catch (err) {
      console.error(err);
      messageApi.warning('Inventory API not available – showing demo data');
      const demo = spareparts.slice(0, 8).map((sp) => ({
        key: sp.id,
        sparepartId: sp.id,
        currentQty: Math.floor(Math.random() * minQty),
        partName: sp.partName,
        partNumber: sp.partNumber,
        importerId: sp.importerId,
        measureUnit: sp.measureUnit || 'pcs',
        requiredQty: minQty,
        unitCost: sp.price || 0,
        selected: true,
      }));
      setBulkItems(demo);
    } finally {
      setBulkLoading(false);
    }
  };

  const removeBulkItem = (key) => {
    setBulkItems((prev) => prev.filter((item) => item.key !== key));
  };

  const toggleItemSelection = (key) => {
    setBulkItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, selected: !item.selected } : item))
    );
  };

  const updateBulkItem = (key, field, value) => {
    setBulkItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );
  };

  const onFinish = async (values) => {
    const selectedItems = bulkItems.filter((item) => item.selected);
    if (selectedItems.length === 0) {
      messageApi.warning('Please select at least one item');
      return;
    }

    setLoading(true);
    try {
      const itemsPayload = selectedItems.map((item) => ({
        sparepartId: item.sparepartId,
        quantity: item.requiredQty,
        unitCost: item.unitCost || 0,
      }));

      const totalCost = itemsPayload.reduce((sum, i) => sum + i.quantity * i.unitCost, 0);
      const payload = {
        supplierId: values.supplierId,
        orderDate: values.orderDate?.toISOString() || new Date().toISOString(),
        currency: values.currency || 'USD',
        shippingCost: values.shippingCost || 0,
        shippingTerms: values.shippingTerms,
        description: values.description || 'Bulk purchase order – low stock items',
        items: itemsPayload,
        totalCost,
        purchaseOrderId: `PO-BULK-${Date.now()}`,
      };

      await api.post('/api/purchase-orders', payload);
      messageApi.success('Bulk Purchase Order created successfully');
      navigate('/admin/purchase-order');
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to create bulk Purchase Order');
    } finally {
      setLoading(false);
    }
  };

  const bulkColumns = [
    {
      title: 'Select',
      dataIndex: 'selected',
      width: 60,
      render: (val, record) => (
        <input
          type="checkbox"
          checked={record.selected}
          onChange={() => toggleItemSelection(record.key)}
          style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
        />
      ),
    },
    {
      title: 'Importer ID',
      dataIndex: 'importerId',
      width: 100,
    },
    {
      title: 'Part Name',
      dataIndex: 'partName',
      ellipsis: true,
    },
    {
      title: 'Part Number',
      dataIndex: 'partNumber',
      width: 120,
    },
    {
      title: 'Current Qty',
      dataIndex: 'currentQty',
      width: 100,
      render: (q) => <Tag color={q <= minQty ? 'red' : 'green'}>{q}</Tag>,
    },
    {
      title: 'Required Qty',
      dataIndex: 'requiredQty',
      width: 120,
      render: (val, record) => (
        <InputNumber
          min={1}
          value={val}
          onChange={(v) => updateBulkItem(record.key, 'requiredQty', v || 1)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'UOM',
      dataIndex: 'measureUnit',
      width: 70,
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unitCost',
      width: 110,
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          formatter={(v) => `$ ${v}`}
          parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
          onChange={(v) => updateBulkItem(record.key, 'unitCost', v || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Line Total',
      key: 'lineTotal',
      width: 110,
      render: (_, record) => {
        const total = (record.requiredQty || 0) * (record.unitCost || 0);
        return <Text strong style={{ color: '#52c41a' }}>${total.toFixed(2)}</Text>;
      },
    },
    {
      title: '',
      width: 50,
      render: (_, record) => (
        <MinusCircleOutlined
          style={{ color: '#ff4d4f', fontSize: 18, cursor: 'pointer' }}
          onClick={() => removeBulkItem(record.key)}
        />
      ),
    },
  ];

  const selectedBulkItems = bulkItems.filter((item) => item.selected);
  const totalBulkCost = selectedBulkItems.reduce((sum, item) => sum + (item.requiredQty || 0) * (item.unitCost || 0), 0);

  const steps = [
    { title: 'Load Items', icon: <FilterOutlined /> },
    { title: 'Review & Edit', icon: <ShoppingCartOutlined /> },
    { title: 'Supplier Info', icon: <FileTextOutlined /> },
  ];

  return (
    <div>
      {contextHolder}
  

      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <ShoppingCartOutlined style={{ fontSize: 32, color: '#1890ff' }} />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  Bulk Create Purchase Order
                </Title>
                <Text type="secondary">Automatically create PO for low stock items</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Steps current={currentStep} size="small" style={{ maxWidth: 400 }}>
              {steps.map((step) => (
                <Steps.Step key={step.title} title={step.title} icon={step.icon} />
              ))}
            </Steps>
          </Col>
        </Row>

        <Divider />

        {/* Filter & Load Section */}
        <Card
          size="small"
          style={{ marginBottom: 24, background: '#fafafa', border: '1px solid #d9d9d9' }}
          title={
            <Space>
              <FilterOutlined />
              <Text strong>Step 1: Load Low Stock Items</Text>
            </Space>
          }
        >
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>Show items with quantity ≤</Text>
            </Col>
            <Col>
              <InputNumber
                min={1}
                value={minQty}
                onChange={(v) => setMinQty(v || 10)}
                style={{ width: 100 }}
              />
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                loading={bulkLoading}
                onClick={loadLowStockItems}
              >
                Load Low-Stock Items
              </Button>
            </Col>
            <Col>
              <Text type="secondary">
                {bulkItems.length} item(s) loaded | {selectedBulkItems.length} selected
              </Text>
            </Col>
          </Row>

          {bulkItems.length > 0 && (
            <Alert
              message={`${selectedBulkItems.length} items selected for bulk order`}
              description={`Estimated total: $${totalBulkCost.toFixed(2)}`}
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Card>

        {/* Items Table */}
        <Card
          size="small"
          style={{ marginBottom: 24, background: '#fafafa', border: '1px solid #d9d9d9' }}
          title={
            <Space>
              <ShoppingCartOutlined />
              <Text strong>Step 2: Review & Edit Items</Text>
              {selectedBulkItems.length > 0 && <Tag color="green">{selectedBulkItems.length} selected</Tag>}
            </Space>
          }
        >
          {bulkItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              <FilterOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <p>Click "Load Low-Stock Items" to populate the list</p>
            </div>
          ) : (
            <Table
              columns={bulkColumns}
              dataSource={bulkItems}
              rowKey="key"
              size="small"
              pagination={false}
              scroll={{ y: 400 }}
              style={{ marginBottom: 16 }}
            />
          )}
        </Card>

        {/* Supplier & Additional Info */}
        <Card
          size="small"
          style={{ marginBottom: 24, background: '#fafafa', border: '1px solid #d9d9d9' }}
          title={
            <Space>
              <FileTextOutlined />
              <Text strong>Step 3: Supplier & Shipping Details</Text>
            </Space>
          }
        >
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Row gutter={24}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="supplierId"
                  label="Supplier"
                  rules={[{ required: true, message: 'Please select a supplier' }]}
                >
                  <Select showSearch placeholder="Select supplier">
                    {suppliers.map((s) => (
                      <Option key={s.id} value={s.id}>
                        {s.name} ({s.country})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="orderDate" label="Order Date" initialValue={null}>
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="currency" label="Currency" initialValue="USD">
                  <Select>
                    <Option value="USD">USD - US Dollar</Option>
                    <Option value="EUR">EUR - Euro</Option>
                    <Option value="CNY">CNY - Chinese Yuan</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="shippingCost" label="Shipping Cost" initialValue={0}>
                  <InputNumber
                    min={0}
                    formatter={(v) => `$ ${v}`}
                    parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="shippingTerms" label="Shipping Terms">
                  <Select allowClear placeholder="Select incoterms">
                    <Option value="FOB">FOB - Free on Board</Option>
                    <Option value="CIF">CIF - Cost, Insurance, Freight</Option>
                    <Option value="EXW">EXW - Ex Works</Option>
                    <Option value="DDP">DDP - Delivered Duty Paid</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="description" label="Notes / Description">
                  <TextArea rows={3} placeholder="Special instructions..." />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Divider />

        <Row justify="space-between">
          <Col>
            <Button size="large" icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/purchase-order')}>
              Back to List
            </Button>
          </Col>
          <Col>
            <Space>
              <Button size="large" onClick={() => { setBulkItems([]); form.resetFields(); }}>
                Clear All
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                onClick={() => form.submit()}
                icon={<PlusOutlined />}
                loading={loading}
                disabled={selectedBulkItems.length === 0}
              >
                Create Bulk Purchase Order ({selectedBulkItems.length} items)
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default BulkCreatePurchaseOrder;
